/*
 * WHERE A FLOW'S CARD GOES (Chris, 2026-10-25, again and again: it covered page boxes and the ribbon; it should sit near the ribbon; "considering the ribbon and two page boxes ... so that it doesn't cover any of the
 * boxes ... up down, either side, diagonal, wherever. Just the most optimal placement PLEASE!"; and then, with screenshots, "the first 2 are CLEARLY COVERING UP THE BOXES THEY ARE TRYING TO SHOW and the 3rd is just
 * CLEARLY TOO FAR AWAY"). Two mistakes of mine are corrected here: the card used to be placed once, where the pointer first touched the ribbon, so sliding along a long ribbon left it far behind or on top of the boxes;
 * and the check that measured it only looked at that first moment.
 * Every place for the card within reach of the pointer is weighed, on a grid, and the card takes the cheapest. A place costs, in square pixels: what it hides (the TWO BOXES THE RIBBON JOINS weigh ten times, the
 * hovered ribbon once, every other box a third), plus a charge for each pixel it is from the pointer, plus a little if it is above the pointer's line (below reads better). As the pointer slides along the ribbon the card
 * is weighed again, and it moves only when the new place is clearly better than where it is (so it does not jitter, and a pointer held still never moves it). Pure (no DOM): every number comes in as a rectangle.
 */
export interface Rect { left: number; top: number; right: number; bottom: number }

export interface FlowCardInput {
  /** the card's size */
  size: { width: number; height: number };
  /** the pointer, in the same coordinates as everything else (the window's) */
  pointer: { x: number; y: number };
  /** the boxes the hovered ribbon joins (a landing line: the one box it lands on) */
  linked: Rect[];
  /** every other page box */
  boxes: Rect[];
  /** the hovered ribbon, as small rectangles laid along it */
  ribbon: Rect[];
  /** the window the card must stay inside (`top` is the lowest edge of whatever is above the stage) */
  view: { width: number; height: number; top: number };
  /** how far from the pointer (to the card's nearest edge) a place may be */
  reach?: number;
  /** the space kept between the pointer and the card */
  gap?: number;
  /** where the card is now, if it is shown: it keeps that place unless a new one is better by `margin` */
  current?: { left: number; top: number } | null;
  margin?: number;
}

export interface FlowCardSpot { left: number; top: number; below: boolean; hidden: number; hiddenLinked: number; cost: number; kept: boolean }

const overlap = (a: Rect, b: Rect): number => Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) * Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
const LINKED_WEIGHT = 10;
/** a page box that is not one of the two the ribbon joins: worth keeping clear, but less than being near the ribbon */
const OTHER_WEIGHT = 0.45;
/** square pixels of cost per pixel between the pointer and the card's nearest edge */
const DISTANCE_COST = 45;
/** a card that is not wholly below the pointer's line costs this much more (square pixels): below reads better */
const ABOVE_COST = 800;
const STEP = 10;
/** a new place must beat the one the card is in by this much (square pixels) before the card moves */
const MOVE_MARGIN = 4000;
/** a joined box is kept clear by this much all round (its highlighted outline is drawn outside its rectangle, and a card that merely grazes it still reads as covering it) */
const LINKED_MARGIN = 8;
const padded = (r: Rect, m: number): Rect => ({ left: r.left - m, top: r.top - m, right: r.right + m, bottom: r.bottom + m });

export const chooseFlowCardSpot = ({ size, pointer, linked, boxes, ribbon, view, reach = 300, gap = 12, current = null, margin = MOVE_MARGIN }: FlowCardInput): FlowCardSpot => {
  const { width: w, height: h } = size;
  const maxL = Math.max(8, view.width - 8 - w), maxT = Math.max(view.top, view.height - 8 - h);
  const linkedPadded = linked.map((b) => padded(b, LINKED_MARGIN));
  /** what a place costs for this pointer; null when it is not allowed (on the pointer, or out of reach) */
  const weigh = (rawLeft: number, rawTop: number): FlowCardSpot | null => {
    const left = Math.max(8, Math.min(rawLeft, maxL)), top = Math.max(view.top, Math.min(rawTop, maxT));
    const nearX = Math.max(left, Math.min(pointer.x, left + w)), nearY = Math.max(top, Math.min(pointer.y, top + h));
    const distance = Math.hypot(nearX - pointer.x, nearY - pointer.y);
    if (distance < gap || distance > reach) return null;
    const card: Rect = { left, top, right: left + w, bottom: top + h };
    let hiddenLinked = 0, hiddenOther = 0, hiddenRibbon = 0;
    linkedPadded.forEach((b) => { hiddenLinked += overlap(card, b); });
    boxes.forEach((b) => { hiddenOther += overlap(card, b); });
    ribbon.forEach((r) => { hiddenRibbon += overlap(card, r); });
    const cost = LINKED_WEIGHT * hiddenLinked + OTHER_WEIGHT * hiddenOther + hiddenRibbon + DISTANCE_COST * distance + (top < pointer.y ? ABOVE_COST : 0);

    return { left, top, below: top >= pointer.y, hidden: hiddenLinked + hiddenOther + hiddenRibbon, hiddenLinked, cost, kept: false };
  };
  let best: FlowCardSpot | null = null;
  const fromL = Math.max(8, pointer.x - w - reach), toL = Math.min(maxL, pointer.x + reach);
  const fromT = Math.max(view.top, pointer.y - h - reach), toT = Math.min(maxT, pointer.y + reach);
  for (let left = fromL; left <= toL; left += STEP) {
    for (let top = fromT; top <= toT; top += STEP) {
      const spot = weigh(left, top);
      if (spot && (!best || spot.cost < best.cost)) best = spot;
    }
  }
  if (current) {
    const now = weigh(current.left, current.top);
    // keep the place the card is in while it is still good (not on the pointer, within reach, and not much worse than the best)
    if (now && (!best || now.cost <= best.cost + margin)) return { ...now, left: current.left, top: current.top, kept: true };
  }
  if (best) return best;
  // nothing within reach (a window too small for the card): the usual place, pushed back inside
  const left = Math.max(8, Math.min(pointer.x + gap, maxL)), top = Math.max(view.top, Math.min(pointer.y + gap, maxT));

  return { left, top, below: true, hidden: Number.POSITIVE_INFINITY, hiddenLinked: Number.POSITIVE_INFINITY, cost: Number.POSITIVE_INFINITY, kept: false };
};
